import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import { HelmetProvider } from 'react-helmet-async';
import 'react-toastify/dist/ReactToastify.css';

import RootLayout from './layouts/RootLayout';
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

const HomePage = React.lazy(() => import('./pages/HomePage'));
const ReportPage = React.lazy(() => import('./pages/ReportPage'));
const WorkbenchPage = React.lazy(() => import('./pages/WorkbenchPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const CustomersPage = React.lazy(() => import('./pages/CustomersPage'));
const SearchHistoryPage = React.lazy(() => import('./pages/SearchHistoryPage'));
const RoadmapPage = React.lazy(() => import('./pages/RoadmapPage'));
const UserManualPage = React.lazy(() => import('./pages/UserManualPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const CreghtPage = React.lazy(() => import('./pages/CreghtPage'));
const TrainingCampPage = React.lazy(() => import('./pages/TrainingCampPage'));
const AuthPage = React.lazy(() => import('./pages/oidc/AuthPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 数据保持新鲜状态 5 分钟
      gcTime: 30 * 60 * 1000, // 未使用的数据在缓存中保留 30 分钟
      retry: 2, // 失败重试次数
      refetchOnWindowFocus: false, // 窗口获得焦点时不自动重新获取数据
      refetchOnReconnect: true, // 重新连接时重新获取数据
    },
  },
});

const PageFallback = () => (
  <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
    页面加载中...
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <Router>
              <Suspense fallback={<PageFallback />}>
                <Routes>
                  <Route path="/" element={<RootLayout />}>
                    <Route index element={<HomePage />} />
                    <Route path="report/:id" element={<ReportPage />} />
                    <Route path="history" element={<PrivateRoute><SearchHistoryPage /></PrivateRoute>} />
                    <Route path="workbench" element={<WorkbenchPage />} />
                    <Route path="profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
                    <Route path="customers" element={<PrivateRoute><CustomersPage /></PrivateRoute>} />
                    <Route path="roadmap" element={<RoadmapPage />} />
                    <Route path="user-manual" element={<UserManualPage />} />
                    <Route path="privacy" element={<PrivacyPage />} />
                    <Route path="about" element={<AboutPage />} />
                    <Route path="creght" element={<CreghtPage />} />
                    <Route path="training" element={<TrainingCampPage />} />
                  </Route>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/oidc/auth" element={<AuthPage />} />
                </Routes>
              </Suspense>
            </Router>
            <ToastContainer />
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App; 